import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MarcaLogo from '@/components/feature/MarcaLogo';
import { NOMBRE_FARMACIA, SISTEMA_DESCRIPCION } from '@/lib/marca';

const navItems = [
  {
    path: '/dashboard',
    label: 'Principal',
    icon: 'ri-dashboard-line',
    roles: ['super_admin', 'usuario'],
  },
  {
    path: '/productos',
    label: 'Productos',
    icon: 'ri-medicine-bottle-line',
    roles: ['super_admin', 'usuario'],
  },
  {
    path: '/ventas',
    label: 'Historial de Ventas',
    icon: 'ri-receipt-line',
    roles: ['super_admin', 'usuario'],
  },
  {
    path: '/reportes',
    label: 'Reporte Financiero',
    icon: 'ri-bar-chart-2-line',
    roles: ['super_admin', 'usuario'],
  },
  {
    path: '/usuarios',
    label: 'Usuarios',
    icon: 'ri-user-add-line',
    roles: ['super_admin'],
  },
  {
    path: '/configuracion',
    label: 'Configuración',
    icon: 'ri-settings-3-line',
    roles: ['super_admin'],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowLogoutModal(false);
  };

  const filteredNav = navItems.filter((item) => user && item.roles.includes(user.rol));

  return (
    <div className="min-h-screen flex bg-background-50">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-foreground-800 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 px-6 py-6 border-b border-foreground-700/40">
            <MarcaLogo />
            <div>
              <h2 className="font-heading text-white font-semibold text-lg leading-tight">{NOMBRE_FARMACIA}</h2>
              <p className="text-foreground-400 text-xs">{SISTEMA_DESCRIPCION}</p>
            </div>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-500/15 text-primary-300'
                      : 'text-foreground-300 hover:bg-foreground-700/30 hover:text-white'
                  }`
                }
              >
                <i className={`${item.icon} text-lg`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="px-3 py-4 border-t border-foreground-700/40">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-300 text-xs font-semibold">
                  {user?.nombre?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.nombre}</p>
                <p className="text-foreground-400 text-xs truncate">
                  {user?.rol === 'super_admin' ? 'Super Admin' : 'Usuario'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-300 hover:bg-accent-500/10 hover:text-accent-400 transition-all duration-200 w-full cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line text-lg"></i>
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-background-200">
          <div className="flex items-center justify-between px-4 md:px-6 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>

            <div className="flex items-center gap-4 ml-auto">
              <div className="hidden sm:flex items-center gap-2 text-foreground-500 text-sm">
                <i className="ri-calendar-line"></i>
                <span>{new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-background-100 rounded-full">
                <div className="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-semibold">
                    {user?.nombre?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="text-foreground-700 text-sm font-medium hidden sm:block">{user?.nombre}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {showLogoutModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowLogoutModal(false)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 animate-scale-in">
            <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-r-line text-2xl text-accent-500"></i>
            </div>
            <h3 className="text-center font-heading text-lg font-semibold text-foreground-800 mb-1">
              Cerrar sesión
            </h3>
            <p className="text-center text-sm text-foreground-500 mb-6">
              ¿Estás seguro de que deseas cerrar sesión?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}